import { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import '../utils/pdfWorkerSetup';
import type { TemplateSigningField } from '../types/template';
import { useDsTranslate } from '../context/DsProvider';
import { RECIPIENT_COLORS } from '../utils/recipientColors';
import { BASE_PDF_WIDTH } from '../utils/fieldDimensions';
import styles from './PdfViewer.module.scss';

function getFieldIcon(fieldType: string, t: (key: string, fallback: string) => string): string {
  switch (fieldType) {
    case 'SIGNATURE': return `✍ ${t('K_DS_FIELD_SIGNATURE', 'Signature')}`;
    case 'INITIALS': return 'AB';
    case 'DATE_SIGNED': return `📅 ${t('K_DS_FIELD_DATE', 'Date')}`;
    case 'TEXT': return `T ${t('K_DS_FIELD_TEXT', 'Text')}`;
    case 'NUMBER': return `# ${t('K_DS_FIELD_NUMBER', 'Number')}`;
    case 'PHONE': return `📞 ${t('K_DS_FIELD_PHONE', 'Phone')}`;
    case 'CURRENCY': return `$ ${t('K_DS_FIELD_CURRENCY', 'Currency')}`;
    case 'CHECKBOX': return '☐';
    case 'DROPDOWN': return `▾ ${t('K_DS_FIELD_DROPDOWN', 'Select')}`;
    case 'NAME': return `👤 ${t('K_DS_FIELD_NAME', 'Name')}`;
    case 'EMAIL': return `@ ${t('K_DS_FIELD_EMAIL', 'Email')}`;
    case 'COMPANY': return `🏢 ${t('K_DS_FIELD_COMPANY', 'Company')}`;
    case 'TITLE': return `📋 ${t('K_DS_FIELD_TITLE', 'Title')}`;
    default: return fieldType;
  }
}

interface PdfViewerProps {
  url?: string;
  pageNumber: number;
  fields: TemplateSigningField[];
  allFields: TemplateSigningField[];
  selectedFieldIndex: number | null;
  onFieldSelect: (index: number | null) => void;
  onFieldMove: (index: number, x: number, y: number) => void;
  onFieldPageChange?: (index: number, pageNumber: number) => void;
  zoom: number;
  onDocumentLoad?: (numPages: number) => void;
  onVisiblePageChange?: (pageNumber: number) => void;
}

export function PdfViewer({
  url,
  fields,
  allFields,
  selectedFieldIndex,
  onFieldSelect,
  onFieldMove,
  zoom,
  onDocumentLoad,
  onFieldPageChange,
  onVisiblePageChange,
}: PdfViewerProps) {
  const t = useDsTranslate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<{ index: number; offsetX: number; offsetY: number } | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleDocLoad = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setLoadError(null);
    onDocumentLoad?.(n);
  }, [onDocumentLoad]);

  const handleDocError = useCallback((error: Error) => {
    setLoadError(error.message || t('K_DS_PDF_LOAD_FAILED', 'Failed to load PDF'));
  }, [t]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, field: TemplateSigningField) => {
      e.stopPropagation();
      e.preventDefault();
      const globalIndex = allFields.indexOf(field);
      onFieldSelect(globalIndex);

      const fieldEl = e.currentTarget as HTMLElement;
      const rect = fieldEl.getBoundingClientRect();
      setDragging({
        index: globalIndex,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
      });
    },
    [allFields, onFieldSelect],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging) return;

      const pageEls = containerRef.current?.querySelectorAll('[data-page-number]');
      if (!pageEls) return;

      for (const pageEl of Array.from(pageEls)) {
        const rect = (pageEl as HTMLElement).getBoundingClientRect();
        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          const x = (e.clientX - rect.left - dragging.offsetX) / zoom;
          const y = (e.clientY - rect.top - dragging.offsetY) / zoom;
          const targetPage = parseInt((pageEl as HTMLElement).dataset.pageNumber || '1');

          onFieldMove(dragging.index, Math.max(0, Math.round(x)), Math.max(0, Math.round(y)));

          const currentField = allFields[dragging.index];
          if (currentField && currentField.pageNumber !== targetPage && onFieldPageChange) {
            onFieldPageChange(dragging.index, targetPage);
          }
          break;
        }
      }
    },
    [dragging, onFieldMove, onFieldPageChange, zoom, allFields],
  );

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    if (numPages === 0 || !containerRef.current || !onVisiblePageChange) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const pageNum = parseInt((entry.target as HTMLElement).dataset.pageNumber || '1');
            onVisiblePageChange(pageNum);
          }
        }
      },
      { root: containerRef.current?.closest('[class*="scrollArea"]'), threshold: 0.5 },
    );

    const pageEls = containerRef.current.querySelectorAll('[data-page-number]');
    pageEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [numPages, onVisiblePageChange]);

  const getFieldColor = (recipientIndex: number) =>
    RECIPIENT_COLORS[(recipientIndex - 1) % RECIPIENT_COLORS.length] ?? RECIPIENT_COLORS[0]!;

  if (!url) {
    return (
      <div className={styles.placeholder}>
        <p>{t('K_DS_UPLOAD_DOCUMENT', 'Upload a document to get started')}</p>
      </div>
    );
  }

  const fieldsByPage: Record<number, TemplateSigningField[]> = {};
  for (const field of fields) {
    const page = field.pageNumber || 1;
    if (!fieldsByPage[page]) fieldsByPage[page] = [];
    fieldsByPage[page]!.push(field);
  }

  return (
    <div
      className={styles.continuousWrapper}
      style={{ zoom }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onClick={() => onFieldSelect(null)}
      ref={containerRef}
    >
      <Document
        key={typeof url === 'string' ? url : undefined}
        file={url}
        onLoadSuccess={handleDocLoad}
        onLoadError={handleDocError}
        loading={<div className={styles.loading}>{t('K_DS_LOADING_PDF', 'Loading PDF...')}</div>}
        error={
          <div className={styles.error}>
            <p>{t('K_DS_PDF_LOAD_FAILED', 'Failed to load PDF')}</p>
            {loadError && <p className={styles.errorDetail}>{loadError}</p>}
          </div>
        }
      >
        {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
          <div
            key={pageNum}
            className={styles.pageContainer}
            data-page-number={pageNum}
          >
            <Page
              pageNumber={pageNum}
              width={BASE_PDF_WIDTH}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              loading={
                <div className={styles.loading}>
                  {t('K_DS_RENDERING_PAGE', 'Rendering page {page}...').replace('{page}', String(pageNum))}
                </div>
              }
            />

            {(fieldsByPage[pageNum] || []).map((field) => {
              const globalIdx = allFields.indexOf(field);
              const isSelected = globalIdx === selectedFieldIndex;
              const color = getFieldColor(field.recipientIndex);
              const isCheckbox = field.fieldType === 'CHECKBOX';

              return (
                <div
                  key={`field-${globalIdx}`}
                  className={`${styles.fieldOverlay} ${isSelected ? styles.selected : ''} ${dragging?.index === globalIdx ? styles.dragging : ''} ${isCheckbox ? styles.checkboxOverlay : ''}`}
                  style={{
                    left: field.xPosition,
                    top: field.yPosition,
                    width: field.width,
                    height: field.height,
                    borderColor: color,
                    backgroundColor: `${color}18`,
                  }}
                  onMouseDown={(e) => handleMouseDown(e, field)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className={styles.fieldLabel} style={{ color }}>
                    {field.isReadOnly && '🔒 '}
                    {isCheckbox
                      ? (field.defaultValue === 'true' ? '☑' : '☐')
                      : (field.defaultValue || (field.label && field.label !== field.fieldType)
                        ? (field.defaultValue || field.label)
                        : getFieldIcon(field.fieldType, t))}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </Document>
    </div>
  );
}
